/**
 * World の現在状態（編集が反映された値）を、本物の Roblox `.rbxlx`
 * （XML place file, `<roblox version="4">`）形式の文字列へ書き出すモジュール。
 *
 * 子どもがビルダーで組んだシーンを Roblox Studio に取り込めるようにする目的。
 * 物理挙動の忠実再現ではなく、見た目（位置・寸法・色・マテリアル・透明度）の
 * 移植を狙う。出力 Part は基本 anchored（後述）。
 */

import {
  AVATAR_ID,
  ObjectRuntimeState,
  PreviewWorld,
  RobloxMaterial,
  Vec3,
} from '../types'

/**
 * Roblox マテリアル → Enum.Material の数値トークン対応表。
 * 値は Roblox の Enum.Material 列挙値に一致させている。
 * キーは RobloxMaterial union で網羅されるため、未知名は型で排除される。
 */
const MATERIAL_TOKENS: Readonly<Record<RobloxMaterial, number>> = {
  Plastic: 256,
  SmoothPlastic: 272,
  Neon: 288,
  Wood: 512,
  WoodPlanks: 528,
  Marble: 784,
  Basalt: 788,
  Slate: 800,
  CrackedLava: 804,
  Concrete: 816,
  Limestone: 820,
  Granite: 832,
  Pavement: 836,
  Brick: 848,
  Pebble: 864,
  Cobblestone: 880,
  Rock: 896,
  Sandstone: 912,
  CorrodedMetal: 1040,
  DiamondPlate: 1056,
  Foil: 1072,
  Metal: 1088,
  Grass: 1280,
  LeafyGrass: 1284,
  Sand: 1296,
  Fabric: 1312,
  Snow: 1328,
  Mud: 1344,
  Ground: 1360,
  Asphalt: 1376,
  Salt: 1392,
  Ice: 1536,
  Glacier: 1552,
  Glass: 1568,
  ForceField: 1584,
  Air: 1792,
  Water: 2048,
  Cardboard: 2304,
  Carpet: 2305,
  CeramicTiles: 2306,
  ClayRoofTiles: 2307,
  RoofShingles: 2308,
  Leather: 2309,
  Plaster: 2310,
  Rubber: 2311,
}

/** XML 特殊文字をエスケープする（属性値・テキスト両用）。 */
const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

/**
 * 非有限値（NaN/Infinity）を検出して throw する。
 * サイレントな 0 変換でデータを破壊せず、利用側（BuilderPanel）が失敗を
 * ユーザーに提示できるよう、どのオブジェクトのどのプロパティかを含めて投げる。
 */
const assertFinite = (
  value: number,
  objectId: string,
  property: string,
): number => {
  if (!Number.isFinite(value)) {
    throw new Error(
      `rbxlx 書き出し失敗: ${objectId} の ${property} が非有限値です (${value})`,
    )
  }
  return value
}

/** Roblox の XML 数値表記（有限値検証済みの値を文字列化）。 */
const num = (value: number, objectId: string, property: string): string =>
  String(assertFinite(value, objectId, property))

/** マテリアルからトークンを引く（RobloxMaterial は型で網羅済み）。 */
const materialToken = (material: RobloxMaterial): number =>
  MATERIAL_TOKENS[material]

/**
 * '#rrggbb' から Color3uint8 の符号なし整数を算出する。
 * フォーマット `(255<<24)|(r<<16)|(g<<8)|b` を `>>> 0` で符号なし化。
 * 解釈できない場合は白(255,255,255)にフォールバックする。
 */
const colorToUint = (color: string): number => {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(color.trim())
  const hex = match ? match[1] : 'ffffff'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return ((255 << 24) | (r << 16) | (g << 8) | b) >>> 0
}

/**
 * 最終姿勢 = Y ヨー(rotationY) ∘ 基準向き(orientation) を 3x3 回転行列（行優先
 * R00..R22）に変換する。rbxl 由来の傾き(orientation)を保持したまま、子の Y 回転も
 * 反映するため、ランタイムの描画/物理と同じ合成を行ってから行列化する。
 */
const rotationMatrix = (
  state: ObjectRuntimeState,
  objectId: string,
): readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
] => {
  const yaw = assertFinite(state.rotationY, objectId, 'rotationY')
  const base = state.orientation
  assertFinite(base.x, objectId, 'orientation.x')
  assertFinite(base.y, objectId, 'orientation.y')
  assertFinite(base.z, objectId, 'orientation.z')
  assertFinite(base.w, objectId, 'orientation.w')
  // Qy(yaw) ⊗ base のハミルトン積（physicsController.composeYaw と同一）。
  const half = yaw / 2
  const s = Math.sin(half)
  const c = Math.cos(half)
  const x = c * base.x + s * base.z
  const y = c * base.y + s * base.w
  const z = c * base.z - s * base.x
  const w = c * base.w - s * base.y
  // 単位クォータニオン → 行優先回転行列（右手系、Roblox CFrame と同じ規約）。
  const xx = x * x
  const yy = y * y
  const zz = z * z
  return [
    1 - 2 * (yy + zz),
    2 * (x * y - w * z),
    2 * (x * z + w * y),
    2 * (x * y + w * z),
    1 - 2 * (xx + zz),
    2 * (y * z - w * x),
    2 * (x * z - w * y),
    2 * (y * z + w * x),
    1 - 2 * (xx + yy),
  ]
}

/** インデント済みの 1 行を組み立てる。 */
const line = (indent: number, content: string): string =>
  `${'\t'.repeat(indent)}${content}`

/** `<Vector3 name="...">` プロパティ XML。 */
const vector3Property = (
  name: string,
  v: Vec3,
  indent: number,
  objectId: string,
): string =>
  [
    line(indent, `<Vector3 name="${escapeXml(name)}">`),
    line(indent + 1, `<X>${num(v.x, objectId, `${name}.X`)}</X>`),
    line(indent + 1, `<Y>${num(v.y, objectId, `${name}.Y`)}</Y>`),
    line(indent + 1, `<Z>${num(v.z, objectId, `${name}.Z`)}</Z>`),
    line(indent, '</Vector3>'),
  ].join('\n')

/** `<CoordinateFrame name="CFrame">` プロパティ XML（位置 + 合成姿勢の回転行列）。 */
const cframeProperty = (
  position: Vec3,
  state: ObjectRuntimeState,
  indent: number,
  objectId: string,
): string => {
  const [r00, r01, r02, r10, r11, r12, r20, r21, r22] = rotationMatrix(
    state,
    objectId,
  )
  return [
    line(indent, '<CoordinateFrame name="CFrame">'),
    line(indent + 1, `<X>${num(position.x, objectId, 'position.X')}</X>`),
    line(indent + 1, `<Y>${num(position.y, objectId, 'position.Y')}</Y>`),
    line(indent + 1, `<Z>${num(position.z, objectId, 'position.Z')}</Z>`),
    line(indent + 1, `<R00>${num(r00, objectId, 'R00')}</R00>`),
    line(indent + 1, `<R01>${num(r01, objectId, 'R01')}</R01>`),
    line(indent + 1, `<R02>${num(r02, objectId, 'R02')}</R02>`),
    line(indent + 1, `<R10>${num(r10, objectId, 'R10')}</R10>`),
    line(indent + 1, `<R11>${num(r11, objectId, 'R11')}</R11>`),
    line(indent + 1, `<R12>${num(r12, objectId, 'R12')}</R12>`),
    line(indent + 1, `<R20>${num(r20, objectId, 'R20')}</R20>`),
    line(indent + 1, `<R21>${num(r21, objectId, 'R21')}</R21>`),
    line(indent + 1, `<R22>${num(r22, objectId, 'R22')}</R22>`),
    line(indent, '</CoordinateFrame>'),
  ].join('\n')
}

/**
 * 1 つの ObjectRuntimeState を Part の `<Item>` XML に変換する。
 * Workspace 配下に置くため、Item は indent=2 / Properties=3 / 各 prop=4。
 */
const partItem = (state: ObjectRuntimeState, referent: string): string => {
  const itemIndent = 2
  const propertiesIndent = itemIndent + 1
  const propIndent = propertiesIndent + 1
  // 非表示オブジェクトは Studio 上でも見えず触れない実効値で出力する
  // （visible は rbxlx に対応プロパティが無いため Transparency/CanCollide に写す）。
  const transparency = state.visible ? state.transparency : 1
  const canCollide = state.visible ? state.canCollide : false
  const properties = [
    line(propIndent, `<string name="Name">${escapeXml(state.name)}</string>`),
    vector3Property('size', state.size, propIndent, state.id),
    cframeProperty(state.position, state, propIndent, state.id),
    line(
      propIndent,
      `<Color3uint8 name="Color3uint8">${colorToUint(state.color)}</Color3uint8>`,
    ),
    line(
      propIndent,
      `<token name="Material">${materialToken(state.material)}</token>`,
    ),
    // ビルダー出力は子どもが意図した配置をそのまま保つため anchored を既定とする
    // （未 anchored だと Studio 上で重力落下し、配置意図が崩れるため）。
    line(propIndent, '<bool name="Anchored">true</bool>'),
    line(propIndent, `<bool name="CanCollide">${canCollide}</bool>`),
    line(
      propIndent,
      `<float name="Transparency">${num(transparency, state.id, 'transparency')}</float>`,
    ),
  ].join('\n')

  return [
    line(itemIndent, `<Item class="Part" referent="${escapeXml(referent)}">`),
    line(propertiesIndent, '<Properties>'),
    properties,
    line(propertiesIndent, '</Properties>'),
    line(itemIndent, '</Item>'),
  ].join('\n')
}

/**
 * 各 Item に付与するユニークな referent を生成する。
 * `RBX` + 連番由来の 16 進ゼロ埋めで、1 ファイル内での一意性を保証する。
 */
const makeReferent = (index: number): string =>
  `RBX${index.toString(16).toUpperCase().padStart(16, '0')}`

/**
 * World の現在状態から Roblox `.rbxlx` 文字列を生成する純粋関数（副作用なし）。
 *
 * Place の構造に合わせ、全 Part を `Workspace` サービス配下に入れる。
 * AVATAR_ID は出力から除外する。非有限な座標/サイズ/回転があれば throw する。
 */
export const buildRbxlx = (world: PreviewWorld): string => {
  // referent はファイル内で一意ならよい。index 0 を Workspace、
  // 1 以降を各 Part に割り当てて衝突を避ける。
  const workspaceReferent = makeReferent(0)
  const parts: string[] = []
  let index = 1
  for (const state of world.getState().values()) {
    if (state.id === AVATAR_ID) {
      continue
    }
    parts.push(partItem(state, makeReferent(index)))
    index += 1
  }

  const workspace = [
    line(
      1,
      `<Item class="Workspace" referent="${escapeXml(workspaceReferent)}">`,
    ),
    line(2, '<Properties>'),
    line(3, '<string name="Name">Workspace</string>'),
    line(2, '</Properties>'),
    ...parts,
    line(1, '</Item>'),
  ].join('\n')

  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<roblox version="4">',
    workspace,
    '</roblox>',
    '',
  ].join('\n')
}

/**
 * 生成した `.rbxlx` をブラウザでダウンロードさせる（DOM 副作用あり）。
 * SSR 非対象（document 前提）。
 */
export const downloadRbxlx = (
  world: PreviewWorld,
  filename = 'scene.rbxlx',
): void => {
  const xml = buildRbxlx(world)
  const blob = new Blob([xml], { type: 'application/xml' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  try {
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    // click() 例外時も DOM/Object URL を残さないよう必ず後始末する。
    anchor.remove()
    URL.revokeObjectURL(url)
  }
}
